import { ErrorCode } from './error-codes';

/**
 * Centralized user-friendly messages by error code.
 * No backend raw messages are shown directly; this map controls what user sees.
 * Persian / English can be extended via i18n later.
 */
const MESSAGES = {
    [ErrorCode.VALIDATION_FAILED]: 'لطفا اطلاعات وارد شده را بررسی کنید',
    [ErrorCode.AUTH_REQUIRED]: 'برای ادامه وارد حساب کاربری شوید',
    [ErrorCode.INVALID_TOKEN]: 'نشست شما نامعتبر است. لطفا دوباره وارد شوید',
    [ErrorCode.TOKEN_EXPIRED]: 'نشست شما منقضی شده است. لطفا دوباره وارد شوید',
    [ErrorCode.SESSION_EXPIRED]: 'نشست شما منقضی شده است. لطفا دوباره وارد شوید',
    [ErrorCode.UNAUTHORIZED]: 'احراز هویت لازم است',
    [ErrorCode.FORBIDDEN]: 'دسترسی غیر مجاز',
    [ErrorCode.INSUFFICIENT_PERMISSION]: 'شما مجوز انجام این عملیات را ندارید',
    [ErrorCode.ROLE_REQUIRED]: 'دسترسی شما برای این بخش کافی نیست',
    [ErrorCode.STEP_UP_REQUIRED]: 'برای این عملیات نیاز به تایید مجدد هویت دارید',
    [ErrorCode.NOT_FOUND]: 'مورد درخواستی یافت نشد',
    [ErrorCode.RESOURCE_NOT_FOUND]: 'مورد درخواستی یافت نشد',
    [ErrorCode.USER_NOT_FOUND]: 'کاربر یافت نشد',
    [ErrorCode.CERTIFICATE_NOT_FOUND]: 'گواهی یافت نشد',
    [ErrorCode.PAYMENT_NOT_FOUND]: 'پرداخت یافت نشد',
    [ErrorCode.EKYC_NOT_FOUND]: 'اطلاعات احراز هویت یافت نشد',
    [ErrorCode.EVIDENCE_NOT_FOUND]: 'مدرک یافت نشد',
    [ErrorCode.CONFLICT]: 'درخواست شما با وضعیت فعلی ناسازگار است',
    [ErrorCode.RESOURCE_ALREADY_EXISTS]: 'این مورد قبلا ثبت شده است',
    [ErrorCode.INVALID_STATE_TRANSITION]: 'تغییر وضعیت مجاز نیست',
    [ErrorCode.CERTIFICATE_PENDING]: 'گواهی در انتظار بررسی است',
    [ErrorCode.EKYC_NOT_APPROVED]: 'احراز هویت شما هنوز تایید نشده است',
    [ErrorCode.CSR_INVALID]: 'درخواست گواهی نامعتبر است',
    [ErrorCode.PAYMENT_ALREADY_COMPLETED]: 'پرداخت قبلا انجام شده است',
    [ErrorCode.PAYMENT_FAILED]: 'پرداخت ناموفق بود. لطفا دوباره تلاش کنید',
    [ErrorCode.PAYMENT_GATEWAY_ERROR]: 'خطا در درگاه پرداخت',
    [ErrorCode.PAYMENT_PROVIDER_UNAVAILABLE]: 'درگاه پرداخت موقتا در دسترس نیست. لطفا بعدا تلاش کنید',
    [ErrorCode.PAYMENT_EXPIRED]: 'زمان پرداخت منقضی شده است',
    [ErrorCode.INSUFFICIENT_BALANCE]: 'موجودی کافی نیست',
    [ErrorCode.SMS_PROVIDER_FAILED]: 'ارسال پیامک با اختلال مواجه شد. لطفا دوباره تلاش کنید',
    [ErrorCode.IDENTITY_PROVIDER_TIMEOUT]: 'سرویس احراز هویت پاسخگو نیست',
    [ErrorCode.PKI_PROVIDER_ERROR]: 'سرویس گواهی موقتا در دسترس نیست',
    [ErrorCode.DATABASE_UNAVAILABLE]: 'سرویس موقتا در دسترس نیست. لطفا بعدا تلاش کنید',
    [ErrorCode.TRANSACTION_FAILED]: 'عملیات با خطا مواجه شد. لطفا دوباره تلاش کنید',
    [ErrorCode.RATE_LIMITED]: 'تعداد درخواست‌ها بیش از حد مجاز است. لطفا کمی صبر کنید',
    [ErrorCode.TOO_MANY_REQUESTS]: 'تعداد درخواست‌ها بیش از حد مجاز است. لطفا کمی صبر کنید',
    [ErrorCode.INVALID_OTP]: 'کد یکبار مصرف نامعتبر است',
    [ErrorCode.OTP_EXPIRED]: 'کد یکبار مصرف منقضی شده است',
    [ErrorCode.OTP_RATE_LIMITED]: 'تعداد تلاش‌های شما بیش از حد مجاز است. لطفا بعدا تلاش کنید',
    [ErrorCode.TICKET_NOT_FOUND]: 'تیکت یافت نشد',
    [ErrorCode.INVALID_CREDENTIALS]: 'نام کاربری یا رمز عبور نامعتبر است',
    [ErrorCode.ACCOUNT_DEACTIVATED]: 'حساب کاربری غیرفعال شده است',
    [ErrorCode.ACCOUNT_BLOCKED]: 'حساب کاربری مسدود شده است',
    [ErrorCode.INVALID_NATIONAL_CODE]: 'کد ملی نامعتبر است',
    [ErrorCode.NATIONAL_CODE_TAKEN]: 'این کد ملی قبلا ثبت شده است',
    [ErrorCode.INTERNAL]: 'خطایی رخ داد. لطفا دوباره تلاش کنید',
    [ErrorCode.INTERNAL_ERROR]: 'خطای سرور. لطفا بعدا دوباره تلاش کنید',
    [ErrorCode.UNKNOWN]: 'خطای نامشخص رخ داد',
    NETWORK_ERROR: 'خطا در برقراری ارتباط با سرور',
    NETWORK_TIMEOUT: 'درخواست زمان زیادی طول کشید. لطفا دوباره تلاش کنید',
};

const EN_FALLBACKS = {
    [ErrorCode.VALIDATION_FAILED]: 'Please check the entered information',
    [ErrorCode.SESSION_EXPIRED]: 'Your session expired. Please login again.',
    [ErrorCode.PAYMENT_FAILED]: 'Payment was unsuccessful',
    [ErrorCode.INTERNAL_ERROR]: 'Something went wrong. Please try again.',
};

/**
 * Get user-friendly message for an error code.
 * @param {string} code
 * @param {string} fallback - fallback if code not mapped
 * @returns {string}
 */
export function getMessageForCode(code, fallback) {
    if (!code) return fallback || MESSAGES[ErrorCode.UNKNOWN];
    return MESSAGES[code] || fallback || MESSAGES[ErrorCode.UNKNOWN];
}

/**
 * Get message from ApiError instance (prefers mapped code message unless details contain field errors)
 */
export function getErrorMessage(error, fallback = 'خطای نامشخص') {
    if (!error) return fallback;
    // ApiError
    if (error.code) {
        // For validation, prefer field-level details if available? Caller can handle details separately
        return getMessageForCode(error.code, error.message || fallback);
    }
    // Legacy shape
    if (error.message) return error.message;
    return fallback;
}

/**
 * For validation errors, return field-level map to display under inputs.
 * Backend sends { details: { phone: ['Invalid...'], nationalCode: [...] } } or { details: { errors: [...] } }
 */
export function getValidationDetails(error) {
    if (!error) return null;
    const details = error.details;
    if (!details) return null;
    if (typeof details === 'object' && !Array.isArray(details)) {
        // Already a field map
        return details;
    }
    return null;
}

export default { getMessageForCode, getErrorMessage, getValidationDetails, MESSAGES };
